// src/utils/orderActivityLogger.js

import { collection, doc, serverTimestamp, writeBatch } from 'firebase/firestore';
import { db } from '../firebase';

/**
 * Comprehensive Order Activity Logger
 * Tracks all order-related actions with detailed information
 */

// Activity Types
export const ACTIVITY_TYPES = {
  ORDER_CREATED: 'siparis_olusturuldu',
  ORDER_UPDATED: 'siparis_guncellendi',
  ORDER_DELETED: 'siparis_silindi',
  ORDER_STATUS_CHANGED: 'siparis_durum_degisti',
  PAYMENT_RECEIVED: 'odeme_alindi',
  SHIPMENT_CREATED: 'sevkiyat_olusturuldu',
  ORDER_CLOSED: 'siparis_kapatildi',
  ITEMS_ADDED: 'urun_eklendi',
  ITEMS_REMOVED: 'urun_cikarildi',
  ITEMS_UPDATED: 'urun_guncellendi'
};

// Activity Messages in Turkish
export const ACTIVITY_MESSAGES = {
  [ACTIVITY_TYPES.ORDER_CREATED]: 'Sipariş oluşturuldu',
  [ACTIVITY_TYPES.ORDER_UPDATED]: 'Sipariş güncellendi',
  [ACTIVITY_TYPES.ORDER_DELETED]: 'Sipariş silindi',
  [ACTIVITY_TYPES.ORDER_STATUS_CHANGED]: 'Sipariş durumu değiştirildi',
  [ACTIVITY_TYPES.PAYMENT_RECEIVED]: 'Ödeme alındı',
  [ACTIVITY_TYPES.SHIPMENT_CREATED]: 'Sevkiyat oluşturuldu',
  [ACTIVITY_TYPES.ORDER_CLOSED]: 'Sipariş kapatıldı',
  [ACTIVITY_TYPES.ITEMS_ADDED]: 'Ürün eklendi',
  [ACTIVITY_TYPES.ITEMS_REMOVED]: 'Ürün çıkarıldı',
  [ACTIVITY_TYPES.ITEMS_UPDATED]: 'Ürün bilgileri güncellendi'
};

/**
 * Log order activity to Firestore
 * @param {object} params - Activity parameters
 * @param {string} params.companyId - Company ID
 * @param {string} params.orderId - Order ID
 * @param {object} params.user - Current user object
 * @param {string} params.activityType - Type of activity (from ACTIVITY_TYPES)
 * @param {object} params.details - Additional details about the activity
 * @param {object} params.batch - Optional Firestore batch to use
 * @returns {Promise} - Promise that resolves when activity is logged
 */
export const logOrderActivity = async ({
  companyId,
  orderId,
  user,
  activityType,
  details = {},
  batch = null
}) => {
  if (!companyId || !orderId || !user || !activityType) {
    console.error('Missing required parameters for order activity logging');
    return;
  }

  try {
    const shouldCommitBatch = !batch;
    const activityBatch = batch || writeBatch(db);

    // Create activity log entry
    const activityRef = doc(collection(db, 'companies', companyId, 'orders', orderId, 'activities'));
    
    const activityData = {
      // User Information
      userId: user.uid,
      userEmail: user.email,
      userName: user.email?.split('@')[0] || 'Bilinmeyen Kullanıcı',
      userRole: details.userRole || 'kullanıcı',
      
      // Activity Information
      activityType,
      activityMessage: ACTIVITY_MESSAGES[activityType] || 'Bilinmeyen işlem',
      
      // Timestamp
      timestamp: serverTimestamp(),
      formattedDate: new Date().toLocaleString('tr-TR', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      }),
      
      // Order Context
      orderId,
      orderNumber: details.orderNumber || orderId.substring(0, 6),
      
      // Activity Details
      details: {
        ...details,
        ipAddress: details.ipAddress || 'N/A',
        userAgent: details.userAgent || navigator?.userAgent?.substring(0, 100) || 'N/A'
      },
      
      // Additional Metadata
      actionSource: 'web_app',
      version: '1.0'
    };

    activityBatch.set(activityRef, activityData);

    // Also log to company-wide activity log for admin tracking
    const companyActivityRef = doc(collection(db, 'companies', companyId, 'activityLog'));
    const companyActivityData = {
      ...activityData,
      entityType: 'order',
      entityId: orderId,
      action: `order_${activityType}`,
      description: `${ACTIVITY_MESSAGES[activityType]} - Sipariş #${details.orderNumber || orderId.substring(0, 6)}`
    };

    activityBatch.set(companyActivityRef, companyActivityData);

    if (shouldCommitBatch) {
      await activityBatch.commit();
    }

    console.log(`Order activity logged: ${activityType} for order ${orderId}`);
  } catch (error) {
    console.error('Error logging order activity:', error);
  }
};

/**
 * Helper functions for specific order activities
 */

export const logOrderCreated = async (companyId, orderId, user, orderData) => {
  await logOrderActivity({
    companyId,
    orderId,
    user,
    activityType: ACTIVITY_TYPES.ORDER_CREATED,
    details: {
      customerName: orderData.customerName,
      totalAmount: orderData.total,
      currency: orderData.currency,
      itemCount: orderData.lineItems?.length || 0,
      orderNumber: orderData.orderNumber || orderId.substring(0, 6),
      userRole: orderData.userRole || 'kullanıcı'
    }
  });
};

export const logOrderUpdated = async (companyId, orderId, user, changes, batch = null) => {
  await logOrderActivity({
    companyId,
    orderId,
    user,
    activityType: ACTIVITY_TYPES.ORDER_UPDATED,
    details: {
      changes,
      changeCount: Object.keys(changes).length,
      fieldsChanged: Object.keys(changes).join(', '),
      userRole: changes.userRole || 'kullanıcı'
    },
    batch
  });
};

export const logOrderDeleted = async (companyId, orderId, user, orderData, batch = null) => {
  await logOrderActivity({
    companyId,
    orderId,
    user,
    activityType: ACTIVITY_TYPES.ORDER_DELETED,
    details: {
      customerName: orderData.customerName,
      totalAmount: orderData.total,
      currency: orderData.currency,
      deleteReason: orderData.deleteReason || 'Kullanıcı talebi',
      userRole: orderData.userRole || 'kullanıcı'
    },
    batch
  });
};

export const logPaymentReceived = async (companyId, orderId, user, paymentData, batch = null) => {
  await logOrderActivity({
    companyId,
    orderId,
    user,
    activityType: ACTIVITY_TYPES.PAYMENT_RECEIVED,
    details: {
      paymentAmount: paymentData.amount,
      currency: paymentData.currency,
      paymentMethod: paymentData.method || 'Nakit',
      customerName: paymentData.customerName,
      transactionId: paymentData.transactionId,
      userRole: paymentData.userRole || 'kullanıcı'
    },
    batch
  });
};

export const logOrderStatusChanged = async (companyId, orderId, user, statusData, batch = null) => {
  await logOrderActivity({
    companyId,
    orderId,
    user,
    activityType: ACTIVITY_TYPES.ORDER_STATUS_CHANGED,
    details: {
      oldStatus: statusData.oldStatus,
      newStatus: statusData.newStatus,
      reason: statusData.reason || 'Manuel değişiklik',
      userRole: statusData.userRole || 'kullanıcı'
    },
    batch
  });
};

export const logShipmentCreated = async (companyId, orderId, user, shipmentData, batch = null) => {
  await logOrderActivity({
    companyId,
    orderId,
    user,
    activityType: ACTIVITY_TYPES.SHIPMENT_CREATED,
    details: {
      shipmentItems: shipmentData.items?.map(item => ({
        productName: item.name,
        quantity: item.quantity
      })) || [],
      cargoCompany: shipmentData.cargoCompany,
      trackingNumber: shipmentData.trackingNumber,
      totalItems: shipmentData.items?.length || 0,
      userRole: shipmentData.userRole || 'kullanıcı'
    },
    batch
  });
};

export const logItemsChanged = async (companyId, orderId, user, itemChanges, batch = null) => {
  const { added = [], removed = [], updated = [] } = itemChanges;
  
  if (added.length > 0) {
    await logOrderActivity({
      companyId,
      orderId,
      user,
      activityType: ACTIVITY_TYPES.ITEMS_ADDED,
      details: {
        addedItems: added.map(item => ({
          productName: item.name || item.productName,
          quantity: item.quantity,
          price: item.price
        })),
        itemCount: added.length,
        userRole: itemChanges.userRole || 'kullanıcı'
      },
      batch
    });
  }

  if (removed.length > 0) {
    await logOrderActivity({
      companyId,
      orderId,
      user,
      activityType: ACTIVITY_TYPES.ITEMS_REMOVED,
      details: {
        removedItems: removed.map(item => ({
          productName: item.name || item.productName,
          quantity: item.quantity,
          price: item.price
        })),
        itemCount: removed.length,
        userRole: itemChanges.userRole || 'kullanıcı'
      },
      batch
    });
  }

  if (updated.length > 0) {
    await logOrderActivity({
      companyId,
      orderId,
      user,
      activityType: ACTIVITY_TYPES.ITEMS_UPDATED,
      details: {
        updatedItems: updated.map(item => ({
          productName: item.name || item.productName,
          changes: item.changes || {}
        })),
        itemCount: updated.length,
        userRole: itemChanges.userRole || 'kullanıcı'
      },
      batch
    });
  }
};

const orderActivityLogger = {
  logOrderActivity,
  logOrderCreated,
  logOrderUpdated,
  logOrderDeleted,
  logPaymentReceived,
  logOrderStatusChanged,
  logShipmentCreated,
  logItemsChanged,
  ACTIVITY_TYPES,
  ACTIVITY_MESSAGES
};

export default orderActivityLogger;